import React from 'react';
import PropTypes from 'prop-types';
import { parseISO, format } from 'date-fns';
import WalletCardView from '../WalletCard/WalletCard.view';
import { formatPriceToParts } from '../../../util/localize';

import { isExpired, isExpiringSoon } from './WalletDetails.utils';

import {
  Panel,
  SectionTitle,
  DetailsList,
  DetailRow,
  DetailLeft,
  DetailLabel,
  DetailMeta,
  DetailRight,
  Footnote,
  EmptyState
} from './WalletDetails.styled';

/**
 * WalletDetails
 * Renders the wallet "Store Credit" panel with empty, single, expiring, and multiple states.
 *
 * @param {Object[]} issuances - List of credit lines. Each item: { id, label, amount, expiresOn (YYYY-MM-DD) }.
 * @param {string} accountCode - The masked code to show under "Your Store Credit".
 * @param {string} currency - ISO currency code for formatting (default "USD").
 * @param {number} expiringSoonDays - Days threshold to show expiration in danger style (default 14).
 * @param {function} onClose - Close handler for the X button.
 */
const WalletDetails = ({
  issuances,
  accountCode,
  currency,
  expiringSoonDays
}) => {
  const total = (issuances ?? []).reduce(
    (s, it) => s + (Number(it.amount) || 0),
    0
  );

  return (
    <Panel role="dialog" aria-labelledby="store-credit-title">
      <WalletCardView
        code={accountCode}
        showDetails={false}
        balance={total}
        currency={currency}
      />

      <SectionTitle>Credit Details</SectionTitle>

      {issuances.length === 0 ? (
        <EmptyState>No credit available right now.</EmptyState>
      ) : (
        <>
          <DetailsList>
            {issuances.map((it) => (
              <DetailRow key={it.id}>
                <DetailLeft>
                  <DetailLabel>{it.label}</DetailLabel>
                  {it.expiresOn && (
                    <DetailMeta
                      $danger={
                        isExpired(it.expiresOn) ||
                        isExpiringSoon(it.expiresOn, expiringSoonDays)
                      }
                    >
                      {isExpired(it.expiresOn) ? (
                        <>
                          Expired {format(parseISO(it.expiresOn), 'MM/dd/yyyy')}
                        </>
                      ) : (
                        <>
                          Expires {format(parseISO(it.expiresOn), 'MM/dd/yyyy')}
                        </>
                      )}
                    </DetailMeta>
                  )}
                </DetailLeft>
                <DetailRight>
                  {formatPriceToParts(it.amount, currency)}
                </DetailRight>
              </DetailRow>
            ))}
          </DetailsList>
          <Footnote>
            *Credit shown in {currency} and will convert to local currency at
            checkout.
          </Footnote>
        </>
      )}
    </Panel>
  );
};

WalletDetails.propTypes = {
  issuances: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      expiresOn: PropTypes.string
    })
  ),
  accountCode: PropTypes.string,
  currency: PropTypes.string,
  expiringSoonDays: PropTypes.number
};

WalletDetails.defaultProps = {
  currency: 'USD',
  expiringSoonDays: 14,
  issuances: [],
  accountCode: ''
};

export default WalletDetails;
